/**
 * vessel-bridge.ts
 * Centralised vessel-sync for the micro-apps shell.
 *
 * Responsibilities:
 *  1. Shell → iframes  : when the VesselDropdown fires `vesselSyncChanged`,
 *     broadcast VESSEL_CHANGED to every registered iframe.
 *  2. iframe → shell   : when any iframe sends `vessel_changed`, update
 *     localStorage AND re-broadcast VESSEL_CHANGED to all OTHER iframes
 *     (cross-iframe sync) — skipping the one that originated the change.
 *
 * Usage in OptimizedIframe:
 *   const id = useRef(crypto.randomUUID());
 *   useEffect(() => {
 *     registerIframe(id.current, iframeRef);
 *     return () => unregisterIframe(id.current);
 *   }, []);
 *
 * Call initVesselBridge() once at app startup (e.g. in _app.tsx).
 */

import type { RefObject } from 'react';
import { applyVesselHandover } from '../utils/vesselHandover';
import { repostLastVisitedRoute } from '../utils/lastVisitedRoute';
import { logAccess, reportError } from '@/observability/clientLogger';

// ── types ─────────────────────────────────────────────────────────────────────

interface VesselPayload {
  vesselId: string;
  vesselName: string;
  imoList: string;   // comma-separated
  vesselLevel: string;
  /** Matches VesselDropdown option.value for group/DOC selections. */
  vesselCode?: string;
  imo?: string;
}

interface IframeVesselPayload {
  id: string;
  name: string;
  imo: string;
  imoList?: string[];  // full IMO list sent by vessel-sync.js for groups/custom groups
  level?: string;
}

// ── registry ──────────────────────────────────────────────────────────────────

const registry = new Map<string, RefObject<HTMLIFrameElement | null>>();

// `HTMLIFrameElement | null`, because that is what useRef<HTMLIFrameElement>(null)
// produces under React 19's types — and the ref is genuinely null until the iframe
// mounts, which every reader here already handles.
export function registerIframe(id: string, ref: RefObject<HTMLIFrameElement | null>): void {
  registry.set(id, ref);
}

export function unregisterIframe(id: string): void {
  registry.delete(id);
}

// ── helpers ───────────────────────────────────────────────────────────────────

function resolveLevel(level: string | undefined, id: string): string {
  const v = level ?? '';
  if (['MyVessels', 'GROUP', 'FLEET', 'OWNER', 'DOC'].includes(v)) return 'GROUP';
  if (v === 'CUSTOM_GROUP') return 'CUSTOM_GROUP';
  if (id === 'MY_VESSELS') return 'GROUP';
  if (id.startsWith('custom_group')) return 'CUSTOM_GROUP';
  return 'VESSEL';
}

/** UI level stored in vesselDropdown_activeGroup — keep Doc/Fleet/Owner distinct. */
function resolveUiLevel(level: string | undefined, id: string): string {
  const v = (level ?? '').toUpperCase();
  if (v === 'CUSTOM_GROUP' || id.startsWith('custom_group')) return 'CUSTOM_GROUP';
  if (id === 'MY_VESSELS' || v === 'MYVESSELS') return 'MyVessels';
  if (v === 'DOC' || v === 'FLEET' || v === 'OWNER' || v === 'GROUP') return v === 'GROUP' ? 'GROUP' : v;
  return 'VESSEL';
}

/** Same value VesselDropdown uses for option.value / activeGroupValue. */
function vesselOptionValue(v: {
  id: string;
  vesselCode?: string | null;
  imo?: string | number;
}): string {
  return (
    (v.vesselCode && String(v.vesselCode)) ||
    (v.imo != null && v.imo !== '' ? String(v.imo) : '') ||
    v.id
  );
}

function writeActiveGroup(record: Record<string, unknown>): void {
  const existing = JSON.parse(localStorage.getItem('vesselDropdown_activeGroup') ?? '{}') as Record<
    string,
    unknown
  >;
  const id = String(record.id ?? '');
  const sameGroup = existing.id === id;
  const merged = {
    ...(sameGroup ? existing : {}),
    ...record,
    vesselCode: vesselOptionValue({
      id,
      vesselCode:
        (sameGroup ? (existing.vesselCode as string | null | undefined) : undefined) ||
        (record.vesselCode as string | null | undefined),
      imo:
        (record.imo as string | number | undefined) ??
        (sameGroup ? (existing.imo as string | number | undefined) : undefined),
    }),
    level: (record.level as string | undefined) ?? (sameGroup ? existing.level : undefined),
  };
  localStorage.setItem('vesselDropdown_activeGroup', JSON.stringify(merged));
}

function broadcastToIframes(payload: VesselPayload, excludeId?: string): void {
  registry.forEach((ref, id) => {
    if (id === excludeId) return;
    const cw = ref.current?.contentWindow;
    if (!cw) return;
    cw.postMessage({ target: 'siya-embed', type: 'VESSEL_CHANGED', data: payload }, '*');
  });

  // Also broadcast to any iframe in the DOM that is not in the registry
  // (e.g. embed iframes rendered without OptimizedIframe / registerIframe).
  const registeredWindows = new Set<Window>();
  registry.forEach((ref) => { if (ref.current?.contentWindow) registeredWindows.add(ref.current.contentWindow); });

  document.querySelectorAll<HTMLIFrameElement>('iframe').forEach((iframe) => {
    const cw = iframe.contentWindow;
    if (!cw || registeredWindows.has(cw)) return;
    cw.postMessage({ target: 'siya-embed', type: 'VESSEL_CHANGED', data: payload }, '*');
  });
}

function broadcastClearToIframes(): void {
  const seen = new Set<Window>();
  const post = (cw: Window | null | undefined): void => {
    if (!cw || seen.has(cw)) return;
    seen.add(cw);
    cw.postMessage({ target: 'siya-embed', type: 'VESSEL_CLEARED', data: null }, '*');
  };
  registry.forEach((ref) => post(ref.current?.contentWindow));
  document.querySelectorAll<HTMLIFrameElement>('iframe').forEach((iframe) => post(iframe.contentWindow));
}

function resolveImoList(payload: IframeVesselPayload): string[] {
  // Prefer the full imoList sent by vessel-sync.js (groups carry all IMOs now)
  if (payload.imoList && payload.imoList.length > 0) return payload.imoList;
  // For the same vessel, preserve the existing imoList (avoids losing group members on echo-back)
  try {
    const existing = JSON.parse(localStorage.getItem('vesselDropdown_activeGroup') ?? '{}');
    if (existing.id === payload.id && (existing.imoList?.length ?? 0) > 0) return existing.imoList;
  } catch { /* ignore */ }
  return payload.imo ? [payload.imo] : [];
}

function updateLocalStorage(payload: IframeVesselPayload, imoList: string[]): void {
  try {
    const level = resolveLevel(payload.level, payload.id);

    // Only overwrite the active group when a group/fleet/DOC is selected. When an
    // individual vessel is picked from inside a group, preserve the existing group
    // so its filtered member list — and cross-app restore — keep the group context.
    if (level !== 'VESSEL') {
      const uiLevel = resolveUiLevel(payload.level, payload.id);
      writeActiveGroup({
        id: payload.id,
        vesselName: payload.name,
        vesselCode: vesselOptionValue({
          id: payload.id,
          imo: payload.imo,
        }),
        imo: payload.imo ?? '',
        imoList,
        level: uiLevel,
      });
    }
    localStorage.setItem('vesselSync_activeVessel', JSON.stringify({
      id: payload.id,
      vesselName: payload.name,
      imo: payload.imo ?? '',
      imoList,
      level,
    }));
    localStorage.setItem('selectedUserVesselId', payload.id);
  } catch (e) {
    reportError(e, { event_detail: 'vessel_bridge_storage_write_failed', direction: 'iframe_to_shell', vesselId: payload.id });
  }
}

// ── init ──────────────────────────────────────────────────────────────────────

let initialised = false;
let broadcastFromIframe = false;

export function initVesselBridge(): void {
  if (initialised) return;
  initialised = true;

  // 0. Adopt the vessel this page was opened WITH, before anything reads storage.
  //
  // The chat's open-in-new-tab loads this shell as a top-level page, and Safari does not
  // give that tab the localStorage the chat-embedded iframe wrote — so the tab would
  // otherwise open on whatever vessel it last held on its own, which is the wrong one.
  // The vessel rides in on the URL instead; see utils/vesselHandover. This runs at module
  // scope in _app, so the keys are correct before the VesselDropdown restores from them.
  applyVesselHandover();

  // 1. VesselDropdown changed → update localStorage + push VESSEL_CHANGED to all registered iframes.
  // We update BOTH keys here so that any iframe that navigates here immediately after the vessel
  // change reads the correct vessel from either key (vesselDropdown_activeGroup is not written by
  // OverviewDashboard, only by vessel_changed back-pings — so it goes stale without this write).
  window.addEventListener('vesselSyncChanged', (e) => {
    const detail = (e as CustomEvent<VesselPayload>).detail;
    if (!detail?.vesselId) return;

    // Hoisted out of the try below: both are pure derivations that cannot throw,
    // and the access line at the end of this handler needs them. The try covers
    // the localStorage writes, which are what actually fail (quota, storage
    // disabled) — widening it to cover these would say the derivation is risky.
    const imoArr = String(detail.imoList ?? '').split(',').filter(Boolean);
    const level = resolveLevel(detail.vesselLevel, detail.vesselId);

    try {
      // Carry the single imo for VESSEL selections so cross-app restore (which keys
      // off imo for in-group members) and the sidebar's ?imo= can resolve it. Groups
      // have no single imo — they use imoList.
      const imo = level === 'VESSEL' ? (imoArr[0] ?? '') : '';
      const vesselData = { id: detail.vesselId, vesselName: detail.vesselName, imo, imoList: imoArr, level };
      localStorage.setItem('vesselSync_activeVessel', JSON.stringify(vesselData));
      // Preserve the active group when an individual vessel is picked from inside
      // a group — only group/fleet/DOC selections replace it (see updateLocalStorage).
      if (level !== 'VESSEL') {
        writeActiveGroup({
          id: detail.vesselId,
          vesselName: detail.vesselName,
          vesselCode: detail.vesselCode,
          imo: detail.imo ?? imo,
          imoList: imoArr,
          level: resolveUiLevel(detail.vesselLevel, detail.vesselId),
        });
      }
      localStorage.setItem('selectedUserVesselId', detail.vesselId);
    } catch (err) {
      reportError(err, { event_detail: 'vessel_bridge_storage_write_failed', direction: 'shell_sync', vesselId: detail.vesselId });
    }
    // Keep the parent chat's copy of the route+vessel current, so its open-in-new-tab
    // URL names the vessel on screen rather than the one selected before this change.
    repostLastVisitedRoute();

    if (broadcastFromIframe) {
      return;
    }
    // The user picked a vessel in the shell. `iframeCount: 0` separates "nobody
    // was listening" from "the broadcast never happened", which look identical
    // downstream when an App fails to follow the selection.
    logAccess('shell_vessel_selected', {
      vesselId: detail.vesselId, level, imoCount: imoArr.length, iframeCount: registry.size,
    });
    broadcastToIframes(detail);
  });

  // 2. VesselDropdown cleared every vessel → drop the shell's own copy.
  //
  // Without this the shell keeps the last snapshot and re-seeds the removed
  // vessel: OptimizedIframe builds every iframe src from `vesselSync_activeVessel`
  // and EnhancedOverlaySidebar appends its imo to every nav link, so a cleared
  // vessel reappears the moment the user opens an embed or clicks through the
  // sidebar. The dropdown already removed the keys before dispatching; we remove
  // them again so the clear still holds if the reload that follows is prevented,
  // and so any future caller of this event gets the full reset for free.
  window.addEventListener('vesselSyncCleared', () => {
    try {
      localStorage.removeItem('selectedUserVesselId');
      localStorage.removeItem('vesselDropdown_activeGroup');
      localStorage.removeItem('vesselSync_activeVessel');
      localStorage.removeItem('selectedUserVesselIMO');
    } catch (err) {
      reportError(err, { event_detail: 'vessel_bridge_storage_clear_failed' });
    }
    delete (window as any).__pendingIframeVessel;
    // With the vessel gone, the parent must stop replaying it onto the new-tab URL.
    repostLastVisitedRoute();
    logAccess('shell_vessel_cleared', { iframeCount: registry.size });
    broadcastClearToIframes();
  });

  // 3. Cross-tab sync via visibilitychange
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) return;
    try {
      const v = JSON.parse(localStorage.getItem('selectedVessel') ?? 'null') as { id?: string; name?: string; imo?: string; level?: string } | null;
      if (!v?.id || !v?.name) return;
      if (v.id === localStorage.getItem('selectedUserVesselId')) return;
      const imoList = resolveImoList({ id: v.id, name: v.name, imo: v.imo ?? '' });
      const level = resolveLevel(v.level, v.id);
      updateLocalStorage({ id: v.id, name: v.name, imo: v.imo ?? '', level: v.level }, imoList);
      (window as any).__pendingIframeVessel = { id: v.id, name: v.name, imo: v.imo ?? '', imoList, level };
      window.dispatchEvent(new CustomEvent('iframeVesselChanged', { detail: { id: v.id, name: v.name, imo: v.imo ?? '', imoList, level } }));
    } catch (e) {
      // Cross-tab restore: silently swallowing this left the other tab showing a
      // vessel this one had already moved off, with nothing anywhere to say so.
      reportError(e, { event_detail: 'vessel_bridge_cross_tab_restore_failed' });
    }
  });

  // 4. Any iframe changed vessel → update localStorage + re-broadcast to others
  window.addEventListener('message', (event: MessageEvent) => {
    if (!event.data || event.data.target !== 'siya-embed' || event.data.type !== 'vessel_changed') return;

    const payload = event.data.data as IframeVesselPayload | undefined;
    if (!payload?.id || !payload?.name) {
      reportError(new Error('vessel_changed message missing id/name'), {
        event_detail: 'vessel_bridge_malformed_message',
        hasId: !!payload?.id,
        hasName: !!payload?.name,
      });
      return;
    }

    const imoList = resolveImoList(payload);
    const level = resolveLevel(payload.level, payload.id);

    // The other direction: the user changed vessel inside an App and the shell is
    // following. Distinct event, because a sync that only ever fires one way is a
    // real and otherwise-invisible bug.
    logAccess('shell_vessel_synced_from_app', {
      vesselId: payload.id, level, imoCount: imoList.length, iframeCount: registry.size,
    });

    updateLocalStorage(payload, imoList);

    // Store on window so VesselDropdown can apply it on next mount if it was unmounted
    // during the iframe view (e.g. when app-insights "casefiles button" is shown full-screen).
    (window as any).__pendingIframeVessel = { id: payload.id, name: payload.name, imo: payload.imo ?? '', imoList, level };

    // Find which registered iframe sent this message (by comparing contentWindow)
    let sourceId: string | undefined;
    registry.forEach((ref, id) => {
      if (ref.current?.contentWindow === event.source) sourceId = id;
    });

    // Notify any mounted VesselDropdown to update its displayed vessel and refresh App data.
    // Uses a separate event name (iframeVesselChanged) to avoid re-entering the vesselSyncChanged handler.
    window.dispatchEvent(new CustomEvent('iframeVesselChanged', {
      detail: { id: payload.id, name: payload.name, imo: payload.imo ?? '', imoList, level },
    }));

    // Also dispatch vesselSyncChanged so App modules (non-iframe MF components) pick up the vessel change.
    // iframeVesselChanged only updates the shell VesselDropdown UI — modules rely on vesselSyncChanged.
    // broadcastFromIframe flag prevents the vesselSyncChanged handler from re-broadcasting to iframes again.
    broadcastFromIframe = true;
    window.dispatchEvent(new CustomEvent('vesselSyncChanged', {
      detail: { vesselId: payload.id, vesselName: payload.name, imoList: imoList.join(','), vesselLevel: level },
    }));
    broadcastFromIframe = false;

    // Re-broadcast to all OTHER iframes so they stay in sync
    broadcastToIframes(
      { vesselId: payload.id, vesselName: payload.name, imoList: imoList.join(','), vesselLevel: level },
      sourceId,
    );
  });
}
